import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { filter, find, flatMap, map, sum, sumBy } from "lodash";
import dayjs from "dayjs";
import { IoClose, IoAdd } from "react-icons/io5";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { RiDeleteBin6Fill } from "react-icons/ri";
import DatePickerField from "@/Common/DatePickerField";
import { createCart, createTempBooking } from "@/services/api/cart";
import {
  getAuthToken,
  getGuestId,
  setCookie,
  withRegionPath,
} from "@/utils/session";
import styles from "./BookingModal.module.css";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import {
  buildCartRequestPayload,
  buildModuleRoom,
} from "@/utils/bookingHelpers";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { pushAddToCartDataLayer, readCartIdFromResponse } from "@/utils/dataLayer";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";

function createRoom() {
  return { adult: 2, child: 0, childAge: [] };
}

function formatPrice(value, isIndia = true) {
  const amount = Number(value) || 0;
  if (isIndia) return `₹ ${amount.toLocaleString("en-IN")}`;
  return `AED ${amount.toLocaleString("en-AE")}`;
}

function calcTotalPrice(rooms, prices = []) {
  const adultPrice = sum(
    map(
      rooms,
      (room) =>
        room.adult *
        (find(prices, (p) => Number(p.toAdult) === Number(room.adult))?.price ||
          0)
    )
  );

  const childPrice = sum(
    flatMap(rooms, (room) => {
      const priceObj = find(
        prices,
        (p) => Number(p.toAdult) === Number(room.adult)
      );
      return (
        room.childAge?.map((age) => {
          if (age < 2) return priceObj?.infantPrice || 0;
          if (age <= 5) return priceObj?.child1Price || 0;
          return priceObj?.child2Price || 0;
        }) || []
      );
    })
  );

  return adultPrice + childPrice;
}

function buildCartPayload({
  packageId,
  slabId,
  slabPrices = [],
  rooms,
  travelDate,
  couponCode,
}) {
  const token = getAuthToken();
  const tempUserId = token ? "" : getGuestId();

  return buildCartRequestPayload({
    tempUserId,
    token: token || "",
    module: "Holiday",
    travelDate,
    moduleEntries: [
      {
        serviceId: packageId,
        transferId: 0,
        startTime: "00:00:00",
        slabId,
        couponCode: couponCode || "",
        rooms: map(rooms, (room) => {
          const childCount = Number(room.child) || 0;
          const childAges =
            childCount > 0 ? (room.childAge || []).slice(0, childCount) : [];

          return buildModuleRoom({
            optionId:
              find(
                slabPrices,
                (price) => Number(price.toAdult) === Number(room.adult)
              )?.priceId || 0,
            adult: room.adult,
            child: childCount,
            infant: 0,
            ages: childAges.map((age) => ({
              age: Number(age),
              type: Number(age) < 2 ? "Infant" : "Child",
            })),
          });
        }),
      },
    ],
  });
}

export default function BookingModal({
  open,
  onClose,
  title,
  packageDetail,
  itinerary,
  image,
  slabLabel,
  slabId,
  propertyName,
  packageData = [],
  slabPrices = [],
  packageId,
  couponCode = "",
  isDynamicPackage = true,
  isSpecialPackage = false,
  isIndia = true,
  fromDate = "",
  toDate = "",
}) {
  const router = useRouter();
  const [selectedDate, setSelectedDate] = useState("");
  const [rooms, setRooms] = useState([createRoom()]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [guestError, setGuestError] = useState(null);
  const [dateError, setDateError] = useState("");
  const [ageError, setAgeError] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const actionLockRef = useRef(false);

  useBodyScrollLock(open);

  const selectedSlab = useMemo(
    () => packageData.find((item) => item.slabId === slabId) || null,
    [packageData, slabId]
  );

  const activePrices = useMemo(() => {
    if (Array.isArray(slabPrices) && slabPrices.length) return slabPrices;
    return selectedSlab?.prices || [];
  }, [slabPrices, selectedSlab]);

  const minDate = dayjs().add(1, "day").format("YYYY-MM-DD");
  const maxDate = selectedSlab?.toDate || toDate
    ? dayjs(selectedSlab?.toDate || toDate).format("YYYY-MM-DD")
    : "";

  useEffect(() => {
    if (!open) return undefined;

    const defaultDate = isDynamicPackage
      ? minDate
      : selectedSlab?.fromDate || fromDate
        ? dayjs(selectedSlab?.fromDate || fromDate).format("YYYY-MM-DD")
        : minDate;

    setSelectedDate(defaultDate);
    setRooms([createRoom()]);
    setSelectedIndex(0);
    setGuestError(null);
    setDateError("");
    setAgeError("");
    setShowSummary(false);
    setAddToCartLoading(false);
    setBookingLoading(false);
    actionLockRef.current = false;

    return undefined;
  }, [open, isDynamicPackage, minDate, selectedSlab?.fromDate, fromDate]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const selected = rooms[selectedIndex] || rooms[0];
  const totalAdults = sumBy(rooms, "adult");
  const totalChildren = sumBy(rooms, "child");

  const pricingOption = useMemo(() => {
    const list = Array.isArray(packageData) ? packageData : [];
    const bySlab = list.filter(
      (option) =>
        option.slabId === slabId ||
        String(option.slabName) === String(propertyName)
    );
    if (!isDynamicPackage && selectedDate) {
      const byDate = bySlab.filter(
        (option) =>
          dayjs(option.fromDate).format("YYYY-MM-DD") ===
          dayjs(selectedDate).format("YYYY-MM-DD")
      );
      return byDate[0] || bySlab[0] || null;
    }
    return bySlab[0] || null;
  }, [packageData, slabId, propertyName, isDynamicPackage, selectedDate]);

  const totalPrice = useMemo(
    () => calcTotalPrice(rooms, pricingOption?.prices || activePrices),
    [rooms, pricingOption, activePrices]
  );

  if (!open) return null;

  const handleDateChange = (value) => {
    setSelectedDate(value);
    if (!value) {
      setDateError("Travel date is required");
      return;
    }
    const picked = dayjs(value).startOf("day");
    const min = dayjs(minDate).startOf("day");
    if (picked.isBefore(min)) {
      setDateError("Please select a valid date (tomorrow or later)");
    } else {
      setDateError("");
    }
  };

  const handleCountMinus = (name) => {
    setRooms((prev) => {
      const updated = [...prev];
      const current = { ...updated[selectedIndex] };
      const next = current[name] - 1;
      const min = name === "adult" ? 1 : 0;
      if (next < min) return prev;

      current[name] = next;
      if (name === "child") {
        current.childAge = (current.childAge || []).slice(0, next);
      }
      updated[selectedIndex] = current;
      setGuestError(null);
      return updated;
    });
  };

  const handleCountPlus = (name) => {
    setRooms((prev) => {
      const updated = [...prev];
      const current = { ...updated[selectedIndex] };
      const roomTotal = current.adult + current.child;
      if (roomTotal >= 4) {
        setGuestError(selectedIndex + 1);
        return prev;
      }
      if (name === "adult" && current.adult >= 3) return prev;
      if (name === "child" && current.child >= 3) return prev;

      current[name] = current[name] + 1;
      updated[selectedIndex] = current;
      setGuestError(null);
      return updated;
    });
  };

  const handleSelectAge = (childIndex, age) => {
    setRooms((prev) => {
      const updated = [...prev];
      const current = { ...updated[selectedIndex] };
      const childAge = [...(current.childAge || [])];
      childAge[childIndex] = age;
      current.childAge = childAge;
      updated[selectedIndex] = current;
      return updated;
    });
    setAgeError("");
  };

  const handleAddRoom = () => {
    if (rooms.length >= 4) return;
    setRooms((prev) => [...prev, { adult: 1, child: 0, childAge: [] }]);
    setSelectedIndex(rooms.length);
    setGuestError(null);
  };

  const handleRemoveRoom = (indexToDelete) => {
    if (rooms.length <= 1) return;
    setRooms((prev) => prev.filter((_, index) => index !== indexToDelete));
    setSelectedIndex((prev) => {
      if (indexToDelete === prev) return Math.max(0, prev - 1);
      if (indexToDelete < prev) return prev - 1;
      return prev;
    });
    setGuestError(null);
  };

  const handleCalculateAmount = () => {
    if (!selectedDate) {
      setDateError("Travel date is required");
      return;
    }
    if (dateError) return;

    const ages = flatMap(rooms, "childAge").filter((age) => age != null);
    const childCount = sumBy(rooms, "child");
    if (ages.length !== childCount) {
      setAgeError("Please select age for all children");
      return;
    }

    setAgeError("");
    setShowSummary(true);
  };

  const travelDate = isDynamicPackage
    ? selectedDate
    : selectedSlab?.fromDate || fromDate || selectedDate;

  const handleAddToCart = async (type) => {
    if (actionLockRef.current || addToCartLoading || bookingLoading) return;
    if (!packageId || !slabId) {
      alert("Package selection is incomplete.");
      return;
    }
    if (!travelDate) {
      setDateError("Travel date is required");
      return;
    }

    actionLockRef.current = true;
    if (type === "booking") setBookingLoading(true);
    else setAddToCartLoading(true);

    const payload = buildCartPayload({
      packageId,
      slabId,
      slabPrices: pricingOption?.prices || activePrices,
      rooms,
      travelDate,
      couponCode,
    });

    if (type === "booking") {
      setCookie("id", "");
      setCookie("CartId", "");
      setCookie("module", "");
      try {
        const response = await createTempBooking(payload);
        if (String(response?.status || "").toLowerCase() !== "success") {
          alert(response?.message || "Failed to start booking.");
          return;
        }
        setCookie("id", JSON.stringify(response.data));
        setCookie("module", "Holiday");
        onClose?.();
        const bookingPath = couponCode
          ? `/holiday/package-booking?couponCode=${encodeURIComponent(couponCode)}`
          : "/holiday/package-booking";
        router.push(withRegionPath(bookingPath));
      } catch (err) {
        alert(
          err?.response?.data?.message ||
          "Something went wrong while booking."
        );
      } finally {
        actionLockRef.current = false;
        setBookingLoading(false);
      }
      return;
    }

    try {
      const response = await createCart(payload);
      if (String(response?.status || "").toLowerCase() !== "success") {
        alert(response?.message || "Failed to add to cart.");
        return;
      }
      if (response?.data != null) {
        setCookie("CartId", JSON.stringify(response.data));
        setCookie("module", "Holiday");
      }
      await pushAddToCartDataLayer(readCartIdFromResponse(response?.data));
      onClose?.();
      router.push(withRegionPath("/cart"));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
        "Something went wrong while adding to the cart."
      );
    } finally {
      actionLockRef.current = false;
      setAddToCartLoading(false);
    }
  };

  const formattedDate = selectedDate
    ? dayjs(selectedDate).format("DD/MM/YYYY")
    : "";

  return (
    <div className={styles.root} role="presentation">
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close booking modal"
        onClick={onClose}
      />
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Calculate package amount"
      >
        <div className={styles.header}>
          <div className={styles.packageRow}>
            <div className={styles.thumb}>
              {image ? <img src={image} alt={title || "Package"} /> : null}
            </div>
            <div className={styles.packageInfo}>
              <h3 className={styles.title}>{title}</h3>
              <p className={styles.itinerary}>
                {map(packageDetail?.groupedHotels, (hotel, index) => (
                  <span key={index} style={{ width: "100%", fontSize: "12px" }}>
                    <span style={{ fontWeight: "600" }}>
                      {sumBy(
                        filter(
                          packageDetail?.cityList,
                          (city) => city.cityId === hotel?.hotels[0]?.cityId
                        ),
                        "nights"
                      )}
                      N{" "}
                    </span>
                    {hotel.city}
                    {index + 1 !== packageDetail.groupedHotels?.length && (
                      <span> | </span>
                    )}
                  </span>
                ))}
              </p>
              {/* {itinerary ? <p className={styles.itinerary}>{itinerary}</p> : null} */}
              {slabLabel ? <span className={styles.slab}>{slabLabel}</span> : null}
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close"
            onClick={onClose}
          >
            <IoClose size={22} />
          </button>
        </div>

        {!showSummary ? (
          <div className={styles.modalScroll}>
            <div className={styles.body}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Select Travel Date</span>
                {!isDynamicPackage ? (
                  <div className={styles.fixedDate}>
                    {selectedSlab?.fromDate || fromDate
                      ? `${dayjs(selectedSlab?.fromDate || fromDate).format(
                        "DD-MM-YYYY"
                      )} To ${dayjs(selectedSlab?.toDate || toDate).format(
                        "DD-MM-YYYY"
                      )}`
                      : "Dates unavailable"}
                  </div>
                ) : (
                  <div className={styles.dateWrap}>
                    <DatePickerField
                      value={selectedDate}
                      minDate={minDate}
                      maxDate={maxDate || undefined}
                      onChange={handleDateChange}
                    />
                  </div>
                )}
                {dateError ? <p className={styles.errorText}>{dateError}</p> : null}
              </label>

              <div className={styles.roomTabs}>
                <div className={styles.roomTabsList}>
                  {rooms.map((_, index) => (
                    <button
                      key={`room-tab-${index}`}
                      type="button"
                      className={`${styles.roomTab} ${selectedIndex === index ? styles.roomTabActive : ""
                        }`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      Room {index + 1}
                    </button>
                  ))}
                </div>
                {rooms.length > 1 ? (
                  <button
                    type="button"
                    className={styles.deleteRoomBtn}
                    aria-label="Remove room"
                    onClick={() => handleRemoveRoom(selectedIndex)}
                  >
                    <RiDeleteBin6Fill size={16} />
                  </button>
                ) : null}
              </div>

              {guestError === selectedIndex + 1 ? (
                <p className={styles.guestWarn}>
                  Maximum <strong>4 guests</strong> are allowed in this room
                </p>
              ) : null}

              {selected ? (
                <>
                  <div className={styles.counters}>
                    <div className={styles.counterField}>
                      <span className={styles.fieldLabel}>Adults</span>
                      <div className={styles.counter}>
                        <button
                          type="button"
                          aria-label="Decrease adults"
                          onClick={() => handleCountMinus("adult")}
                          disabled={selected.adult <= 1}
                        >
                          <FaMinus size={14} />
                        </button>
                        <span>{selected.adult}</span>
                        <button
                          type="button"
                          className={styles.plus}
                          aria-label="Increase adults"
                          onClick={() => handleCountPlus("adult")}
                          disabled={selected.adult >= 3}
                        >
                          <FaPlus size={14} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.counterField}>
                      <span className={styles.fieldLabel}>Children</span>
                      <div className={styles.counter}>
                        <button
                          type="button"
                          aria-label="Decrease children"
                          onClick={() => handleCountMinus("child")}
                          disabled={selected.child <= 0}
                        >
                          <FaMinus size={14} />
                        </button>
                        <span>{selected.child}</span>
                        <button
                          type="button"
                          className={styles.plus}
                          aria-label="Increase children"
                          onClick={() => handleCountPlus("child")}
                          disabled={selected.child >= 3}
                        >
                          <FaPlus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {map(Array(selected.child), (_, childIndex) => (
                    <div key={`age-${childIndex}`} className={styles.ageBlock}>
                      <span className={styles.fieldLabel}>
                        Age of child {childIndex + 1}
                      </span>
                      <div className={styles.ageRow}>
                        {map(Array(11), (_, age) => {
                          const value = age + 1;
                          const active = selected.childAge?.[childIndex] === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              className={`${styles.ageBtn} ${active ? styles.ageBtnActive : ""
                                }`}
                              onClick={() => handleSelectAge(childIndex, value)}
                            >
                              {value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              ) : null}

              {ageError ? <p className={styles.errorText}>{ageError}</p> : null}

              <div className={styles.addRoomRow}>
                <button
                  type="button"
                  className={styles.addRoomBtn}
                  onClick={handleAddRoom}
                  disabled={rooms.length >= 4}
                >
                  <IoAdd size={16} /> Add Room
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.modalScroll}>
            <div className={styles.summary}>
              <div className={styles.summaryBox}>
                <span className={styles.summaryBoxLabel}>Travel Date & Travelers</span>
                <div className={styles.summaryContent}>
                  <p className={styles.summaryText}>
                    {!isDynamicPackage && (selectedSlab?.fromDate || fromDate) ? (
                      <>
                        {dayjs(selectedSlab?.fromDate || fromDate).format(
                          "DD-MM-YYYY"
                        )}{" "}
                        To{" "}
                        {dayjs(selectedSlab?.toDate || toDate).format(
                          "DD-MM-YYYY"
                        )}
                      </>
                    ) : (
                      formattedDate
                    )}
                    {" | "}
                    {totalAdults} Adult{totalAdults > 1 ? "s" : ""}
                    {totalChildren > 0 ? `, ${totalChildren} Child` : ""}
                  </p>
                  <button
                    type="button"
                    className={styles.changeBtn}
                    onClick={() => setShowSummary(false)}
                  >
                    Change
                  </button>
                </div>
              </div>

              <div className={styles.priceCard}>
                <p className={styles.totalLabel}>Total Price</p>
                {isSpecialPackage ?
                  <p className={styles.totalPrice}>
                    USD {Number(totalPrice || 0).toLocaleString("en-US")}
                  </p>
                  :
                  <p className={styles.totalPrice}>
                    {getCurrencySymbol("main", 16)} {formatIndianPrice(totalPrice)}
                  </p>
                }
              </div>
            </div>
          </div>
        )}

        <div className={styles.modalFooter}>
          {!showSummary ? (
            <button
              type="button"
              className={styles.calculateBtn}
              onClick={handleCalculateAmount}
            >
              Calculate Amount
            </button>
          ) : !isSpecialPackage ? (
            <div className={styles.summaryActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => handleAddToCart("cart")}
                disabled={addToCartLoading || bookingLoading}
              >
                {addToCartLoading ? "Adding..." : "Add to Cart"}
              </button>
              <button
                type="button"
                className={styles.calculateBtn}
                onClick={() => handleAddToCart("booking")}
                disabled={addToCartLoading || bookingLoading}
              >
                {bookingLoading ? "Processing..." : "Continue Booking"}
              </button>
            </div>
          ) : (
            <button type="button" className={styles.calculateBtn}>
              Send Enquiry
            </button>
          )}
        </div>
      </div>
    </div >
  );
}
